import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faPenToSquare,
  faTrashCan,
  faPlus,
  faSpinner,
  faLock,
  faUsersGear,
  faUsers,
  faUserCheck,
  faPercent,
  faGaugeHigh,
  faTrophy,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import { faDiscord } from "@fortawesome/free-brands-svg-icons";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import Modal from "react-modal";
import { Chart } from "react-chartjs-2";
import "chart.js/auto";
import "../scss/index.scss";
import DataLoader from "../components/globalView/DataLoader";
import {
  getSubFees,
  createSubFee,
  updateSubFee,
  deleteSubFee,
  getSubAffiliateFees,
} from "../store/actions";

const DEFAULT_BPS = 30;
const DEFAULT_SUB_BPS = 0;
const MAX_LABEL = 190;

const sliderStyles = {
  trackStyle: { backgroundColor: "#3ad17e", height: 8 },
  handleStyle: {
    borderColor: "#3ad17e",
    height: 24,
    width: 24,
    marginLeft: -12,
    marginTop: -8,
    backgroundColor: "#0f1a14",
    opacity: 1,
  },
  railStyle: { backgroundColor: "rgba(255,255,255,.1)", height: 8 },
};

const handleActionError = (error, fallback) => {
  const status = error?.response?.status;
  const serverMsg = error?.response?.data?.response;
  if (status === 403) {
    toast.error("Sub-Partner Fees are not enabled for your account.");
    return;
  }
  if (status === 450) {
    toast.error(serverMsg || fallback);
    return;
  }
  toast.error(serverMsg || fallback);
};

const copyToClipboard = (value) => {
  if (!value) return;
  navigator.clipboard
    .writeText(value)
    .then(() => toast.success("Copied to clipboard"))
    .catch(() => toast.error("Failed to copy"));
};

const ACCENTS = {
  green: { bg: "rgba(58,209,126,.12)", fg: "#3ad17e" },
  blue: { bg: "rgba(90,162,255,.12)", fg: "#5aa2ff" },
  amber: { bg: "rgba(230,192,106,.12)", fg: "#e6c06a" },
  purple: { bg: "rgba(199,155,255,.12)", fg: "#c79bff" },
};

const StatCard = ({ icon, label, value, sub, accent = "green" }) => {
  const a = ACCENTS[accent] || ACCENTS.green;
  return (
    <div className="spKpi">
      <span className="spKpiIco" style={{ background: a.bg, color: a.fg }}>
        <FontAwesomeIcon icon={icon} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div className="l">{label}</div>
        <div className="v">{value}</div>
        {sub && <div className="s">{sub}</div>}
      </div>
    </div>
  );
};

// Backend may expose per sub-partner volume/profit under different keys.
const VOLUME_KEYS = [
  "volume",
  "totalVolume",
  "volumeUsd",
  "swapVolume",
  "totalVolumeUsd",
];
const PROFIT_KEYS = [
  "profit",
  "totalProfit",
  "earnings",
  "partnerProfit",
  "totalProfitUsd",
];

const pickNum = (obj, keys) => {
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== "" && !Number.isNaN(Number(v))) {
      return Number(v);
    }
  }
  return null;
};

const formatNum = (n) => {
  if (n == null) return "-";
  if (n >= 1000) return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
  return Number(n.toFixed(4)).toString();
};

const TopPanel = ({ title, icon, accent = "amber", items, formatter }) => {
  const a = ACCENTS[accent] || ACCENTS.amber;
  return (
    <div className="panel" style={{ flex: "1 1 320px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <FontAwesomeIcon icon={icon} style={{ color: a.fg }} />
        <span className="pTitle" style={{ fontSize: 14 }}>
          {title}
        </span>
      </div>
      {items.map((it, idx) => (
        <div
          key={it.sf.subAffiliateId || idx}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "10px 0",
            borderTop: idx > 0 ? "1px solid rgba(255,255,255,.06)" : "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: 6,
                background: a.bg,
                color: a.fg,
                fontSize: 11,
                fontWeight: 700,
                fontFamily: "'JetBrains Mono', monospace",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {idx + 1}
            </span>
            <span
              className="cellStrong"
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={it.sf.subLabel || it.sf.subAffiliateId}
            >
              {it.sf.subLabel || it.sf.subAffiliateId}
            </span>
          </div>
          <span className="cellFee">{formatter(it.v)}</span>
        </div>
      ))}
    </div>
  );
};

const modalStyles = {
  overlay: {
    backgroundColor: "rgba(6, 7, 7, 0.78)",
    zIndex: 2000,
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 520,
    maxHeight: "92vh",
    padding: 0,
    background: "transparent",
    border: "none",
    overflow: "auto",
  },
};

const SubFeeForm = ({ initial, onSubmit, onCancel, submitting }) => {
  const [subLabel, setSubLabel] = useState(initial?.subLabel || "");
  const [bps, setBps] = useState(
    initial?.partnerFeeBps !== undefined ? Number(initial.partnerFeeBps) : DEFAULT_BPS
  );
  const [subBps, setSubBps] = useState(
    initial?.subAffiliateFeeBps !== undefined
      ? Number(initial.subAffiliateFeeBps)
      : DEFAULT_SUB_BPS
  );
  const [active, setActive] = useState(
    initial?.active !== undefined ? Number(initial.active) === 1 : true
  );
  const isEdit = Boolean(initial?.subAffiliateId);

  const submit = () => {
    const label = subLabel.trim();
    if (!label) {
      toast.error("Sub-partner name is required.");
      return;
    }
    if (label.length > MAX_LABEL) {
      toast.error(`Sub-partner name must be ${MAX_LABEL} characters or less.`);
      return;
    }
    const payload = {
      subLabel: label,
      partnerFeeBps: Math.round(bps),
      subAffiliateFeeBps: Math.round(subBps),
    };
    if (isEdit) {
      payload.subAffiliateId = initial.subAffiliateId;
      payload.active = active ? 1 : 0;
    }
    onSubmit(payload);
  };

  return (
    <div className="iswModal" style={{ maxWidth: "none" }}>
      <h2 className="mTitle">{isEdit ? "Edit sub-partner" : "Add sub-partner"}</h2>

      <div className="fld" style={{ marginTop: 16 }}>
        <label className="fldLbl">Sub-partner name</label>
        <input
          className="input"
          type="text"
          value={subLabel}
          maxLength={MAX_LABEL}
          onChange={(e) => setSubLabel(e.target.value)}
          placeholder="e.g. OpenOcean"
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <label className="fldLbl" style={{ margin: 0 }}>
          Your fee (bps)
        </label>
        <span style={{ color: "#3ad17e", fontWeight: 700, fontSize: 14 }}>
          {Math.round(bps)} bps ({(bps / 100).toFixed(2)}%)
        </span>
      </div>
      <Slider
        min={0}
        max={500}
        value={bps}
        onChange={(v) => setBps(v)}
        trackStyle={sliderStyles.trackStyle}
        handleStyle={sliderStyles.handleStyle}
        railStyle={sliderStyles.railStyle}
      />
      <p className="fldHelp" style={{ marginTop: 10 }}>
        This is the net amount you keep per swap for this sub-partner. Our
        margin is added on top automatically. Default is {DEFAULT_BPS} bps
        (0.30%).
      </p>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 10,
          marginTop: 22,
        }}
      >
        <label className="fldLbl" style={{ margin: 0 }}>
          Sub-partner fee (bps)
        </label>
        <span style={{ color: "#3ad17e", fontWeight: 700, fontSize: 14 }}>
          {Math.round(subBps)} bps ({(subBps / 100).toFixed(2)}%)
        </span>
      </div>
      <Slider
        min={0}
        max={500}
        value={subBps}
        onChange={(v) => setSubBps(v)}
        trackStyle={sliderStyles.trackStyle}
        handleStyle={sliderStyles.handleStyle}
        railStyle={sliderStyles.railStyle}
      />
      <p className="fldHelp" style={{ marginTop: 10 }}>
        The fee this sub-partner keeps per swap, charged on top. Default is{" "}
        {DEFAULT_SUB_BPS} bps (0.00%).
      </p>

      {isEdit && (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            color: "#cdd5cf",
            marginTop: 18,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          Active
        </label>
      )}

      <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
        <button
          type="button"
          className="btnPrimary"
          style={{ flex: 1 }}
          onClick={submit}
          disabled={submitting}
        >
          {submitting ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <span>{isEdit ? "Save changes" : "Create"}</span>
          )}
        </button>
        <button
          type="button"
          className="btnGhost"
          style={{ flex: 1 }}
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const formatUsd = (n) => {
  const v = Number(n);
  if (n == null || Number.isNaN(v)) return "$0.00";
  return v.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// yyyy-mm-dd (date input value) -> ISO date-time string the backend expects.
const dateInputToIso = (value) => (value ? `${value}T00:00:00Z` : undefined);

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

// Read-only reporting block rendered above the "Your sub-partners" table.
// Shows accrued sub-affiliate fees with a bar chart + top-3 breakdown.
const SubAffiliateFeesReport = () => {
  const dispatch = useDispatch();
  const report = useSelector((state) => state.userReducer.subAffiliateFees);
  const loading = useSelector(
    (state) => state.userReducer.subAffiliateFeesLoading
  );
  const [hidden, setHidden] = useState(false);

  const defaultFrom = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toDateInputValue(d);
  }, []);
  const defaultTo = useMemo(() => toDateInputValue(new Date()), []);

  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  useEffect(() => {
    dispatch(
      getSubAffiliateFees({
        from: dateInputToIso(from),
        to: dateInputToIso(to),
      })
    ).catch((error) => {
      // 403 => feature not enabled: stay quiet and hide the block.
      if (error?.response?.status === 403) {
        setHidden(true);
      }
    });
  }, [dispatch, from, to]);

  const bySub = useMemo(() => {
    const rows = Array.isArray(report?.bySubAffiliate)
      ? report.bySubAffiliate
      : [];
    return [...rows].sort((a, b) => Number(b.fees) - Number(a.fees));
  }, [report]);

  const top3 = useMemo(
    () =>
      bySub.slice(0, 3).map((r) => ({
        sf: { subAffiliateId: r.subAffiliateId, subLabel: r.subLabel },
        v: Number(r.fees) || 0,
      })),
    [bySub]
  );

  const chartData = useMemo(() => {
    const shown = bySub.slice(0, 10);
    return {
      labels: shown.map((r) => r.subLabel || r.subAffiliateId || "—"),
      datasets: [
        {
          label: "Accrued fees (USD)",
          data: shown.map((r) => Number(r.fees) || 0),
          backgroundColor: "#3ad17e",
          borderRadius: 6,
          maxBarThickness: 44,
        },
      ],
    };
  }, [bySub]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#151617",
          borderColor: "rgba(255,255,255,.15)",
          borderWidth: 1,
          callbacks: {
            label: (ctx) => formatUsd(ctx.parsed.y),
          },
        },
      },
      scales: {
        x: {
          ticks: { color: "#7c847e" },
          grid: { color: "rgba(255,255,255,0.04)" },
        },
        y: {
          ticks: {
            color: "#7c847e",
            callback: (value) => formatUsd(value),
          },
          grid: { color: "rgba(255,255,255,0.06)" },
        },
      },
    }),
    []
  );

  if (hidden) return null;

  const totals = report?.totals || {};
  const currency = report?.currency || "USD";

  return (
    <div className="panel" style={{ marginTop: 24 }}>
      <div className="pHd" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <FontAwesomeIcon icon={faChartLine} style={{ color: "#3ad17e" }} />
          <span className="pTitle" style={{ fontSize: 15 }}>
            Sub-Affiliate Fees Report
          </span>
        </div>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          <div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#6e756f",
                marginBottom: 5,
              }}
            >
              From
            </div>
            <input
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              className="input"
              style={{ height: 38, fontSize: 12.5, width: 150 }}
            />
          </div>
          <div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#6e756f",
                marginBottom: 5,
              }}
            >
              To
            </div>
            <input
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              className="input"
              style={{ height: 38, fontSize: 12.5, width: 150 }}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 0",
          }}
        >
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            style={{ color: "#3ad17e", fontSize: 22 }}
          />
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 16,
              marginBottom: 18,
            }}
          >
            <StatCard
              icon={faChartLine}
              label="Accrued fees"
              value={formatUsd(totals.subAffiliateFees)}
              sub={`${currency} · completed swaps`}
              accent="green"
            />
            <StatCard
              icon={faUsers}
              label="Swaps"
              value={Number(totals.swaps || 0).toLocaleString("en-US")}
              sub="Completed"
              accent="blue"
            />
          </div>

          {bySub.length === 0 ? (
            <div className="gtEmpty">
              No accrued sub-affiliate fees in this range.
            </div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
              <div
                style={{
                  flex: "2 1 420px",
                  background: "#0d0e0e",
                  border: "1px solid rgba(255,255,255,.08)",
                  borderRadius: 13,
                  padding: "16px 18px",
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 12.5,
                    color: "#cdd5cf",
                    marginBottom: 14,
                  }}
                >
                  Fees by sub-affiliate
                </div>
                <div style={{ height: 280 }}>
                  <Chart type="bar" data={chartData} options={chartOptions} />
                </div>
              </div>
              {top3.length > 0 && (
                <TopPanel
                  title="Top 3 by fees"
                  icon={faTrophy}
                  accent="amber"
                  items={top3}
                  formatter={formatUsd}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const SubPartnerFees = ({ embedded = false }) => {
  const dispatch = useDispatch();
  const subFees = useSelector((state) => state.userReducer.subFees || []);
  const isLoading = useSelector((state) => state.userReducer.subFeesLoading);
  const subFeesEnabled = useSelector(
    (state) => state.userReducer.user?.response?.subFeesEnabled
  );

  const enabled = Number(subFeesEnabled) === 1;

  const stats = useMemo(() => {
    const total = subFees.length;
    const activeCount = subFees.filter((sf) => Number(sf.active) === 1).length;
    const feeValues = subFees.map((sf) => Number(sf.partnerFeeBps) || 0);
    const avgBps = total
      ? Math.round(feeValues.reduce((a, b) => a + b, 0) / total)
      : 0;
    const maxBps = total ? Math.max(...feeValues) : 0;
    return { total, activeCount, avgBps, maxBps };
  }, [subFees]);

  const { topVolume, topProfit } = useMemo(() => {
    const rank = (keys) =>
      subFees
        .map((sf) => ({ sf, v: pickNum(sf, keys) }))
        .filter((x) => x.v != null && x.v > 0)
        .sort((a, b) => b.v - a.v)
        .slice(0, 3);
    return { topVolume: rank(VOLUME_KEYS), topProfit: rank(PROFIT_KEYS) };
  }, [subFees]);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [createdToken, setCreatedToken] = useState(null);

  useEffect(() => {
    if (enabled) {
      dispatch(getSubFees()).catch((err) =>
        handleActionError(err, "Failed to load sub-partner fees.")
      );
    }
  }, [dispatch, enabled]);

  const openAdd = () => {
    setEditing(null);
    setCreatedToken(null);
    setShowForm(true);
  };

  const openEdit = (subFee) => {
    setEditing(subFee);
    setCreatedToken(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
  };

  const handleSubmit = (payload) => {
    setSubmitting(true);
    const isEdit = Boolean(payload.subAffiliateId);
    const action = isEdit ? updateSubFee(payload) : createSubFee(payload);

    dispatch(action)
      .then((result) => {
        closeForm();
        if (isEdit) {
          toast.success("Sub-partner updated.");
        } else {
          toast.success("Sub-partner created.");
          if (result?.subAffiliateId) {
            setCreatedToken({
              subLabel: payload.subLabel,
              subAffiliateId: result.subAffiliateId,
            });
          }
        }
        // Reload the list so the table/stats reflect the latest data.
        dispatch(getSubFees()).catch((err) =>
          handleActionError(err, "Failed to refresh sub-partner fees.")
        );
      })
      .catch((err) =>
        handleActionError(
          err,
          isEdit ? "Failed to update sub-partner." : "Failed to create sub-partner."
        )
      )
      .finally(() => setSubmitting(false));
  };

  const handleDelete = (subFee) => {
    const label = subFee.subLabel || "this sub-partner";
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;

    dispatch(deleteSubFee(subFee.subAffiliateId))
      .then(() => toast.success("Sub-partner deleted."))
      .catch((err) => handleActionError(err, "Failed to delete sub-partner."));
  };

  if (!enabled) {
    return (
      <>
        {!embedded && (
          <Helmet>
            <title>Sub-Partner Fees | Instaswap Partners</title>
          </Helmet>
        )}
        <div className="acWrap" style={{ maxWidth: 560, marginTop: 40 }}>
          <div className="acCard">
            <div className="wwIco">
              <FontAwesomeIcon icon={faLock} />
            </div>
            <div className="acName">Sub-Partner Fees</div>
            <p
              style={{
                marginTop: 14,
                fontSize: 14.5,
                lineHeight: 1.6,
                color: "#a7b2ab",
              }}
            >
              Issue a dedicated <code>X-Sub-Affiliate-Id</code> for each of your
              downstream partners, set a custom fee per partner, and track
              their swaps and commissions in one place.
            </p>
            <p
              style={{
                marginTop: 12,
                fontSize: 13.5,
                lineHeight: 1.6,
                color: "#7c847e",
              }}
            >
              This feature is not enabled for your account yet. Talk to the
              Instaswap team to unlock per sub-partner fees for your business.
            </p>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                marginTop: 26,
              }}
            >
              <button
                type="button"
                className="btnPrimary"
                onClick={() =>
                  window.open("https://discord.gg/um6NazJSMk", "_blank")
                }
              >
                <FontAwesomeIcon icon={faDiscord} />
                Talk to us on Discord
              </button>
              <a
                href="mailto:support@instaswap.com?subject=Enable%20Sub-Partner%20Fees"
                className="btnGhost"
              >
                Contact support
              </a>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!embedded && (
        <Helmet>
          <title>Sub-Partner Fees | Instaswap Partners</title>
        </Helmet>
      )}
      {isLoading ? (
        <DataLoader />
      ) : (
        <div>
          <div
            className="secHdr"
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 18,
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: "1 1 400px" }}>
              <h2 className="secTitle">Sub-Partner Fees</h2>
              <p className="secLead">
                Manage per sub-partner pricing and share each{" "}
                <code>X-Sub-Affiliate-Id</code> token with your downstream
                sources.
              </p>
            </div>
            <button
              type="button"
              className="btnPrimary sm"
              style={{ flex: "none" }}
              onClick={openAdd}
            >
              <FontAwesomeIcon icon={faPlus} /> Add sub-partner
            </button>
          </div>

          <div className="spKpis">
            <StatCard
              icon={faUsers}
              label="Sub-partners"
              value={stats.total}
              sub="Total configured"
              accent="green"
            />
            <StatCard
              icon={faUserCheck}
              label="Active"
              value={stats.activeCount}
              sub={`${stats.total - stats.activeCount} inactive`}
              accent="blue"
            />
            <StatCard
              icon={faPercent}
              label="Average fee"
              value={`${stats.avgBps} bps`}
              sub={`${(stats.avgBps / 100).toFixed(2)}% avg`}
              accent="amber"
            />
            <StatCard
              icon={faGaugeHigh}
              label="Highest fee"
              value={`${stats.maxBps} bps`}
              sub={`${(stats.maxBps / 100).toFixed(2)}% max`}
              accent="purple"
            />
          </div>

          {(topVolume.length > 0 || topProfit.length > 0) && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 16,
                marginBottom: 8,
              }}
            >
              {topVolume.length > 0 && (
                <TopPanel
                  title="Top 3 by volume"
                  icon={faChartLine}
                  accent="blue"
                  items={topVolume}
                  formatter={formatNum}
                />
              )}
              {topProfit.length > 0 && (
                <TopPanel
                  title="Top 3 by profit"
                  icon={faTrophy}
                  accent="amber"
                  items={topProfit}
                  formatter={formatNum}
                />
              )}
            </div>
          )}

          <SubAffiliateFeesReport />

          <div style={{ marginTop: 24 }}>
            {createdToken && (
              <div
                className="panel"
                style={{
                  marginBottom: 20,
                  borderColor: "rgba(58,209,126,.35)",
                }}
              >
                <div className="pTitle" style={{ marginBottom: 10 }}>
                  Sub-Affiliate-Id for {createdToken.subLabel}
                </div>
                <div className="lwAddrBox" style={{ margin: 0 }}>
                  {createdToken.subAffiliateId}
                  <div className="acts">
                    <button
                      type="button"
                      className="btnGhost sm"
                      onClick={() => copyToClipboard(createdToken.subAffiliateId)}
                    >
                      <FontAwesomeIcon icon={faCopy} /> Copy
                    </button>
                  </div>
                </div>
                <p className="fldHelp" style={{ marginTop: 12 }}>
                  Give this ID to <b style={{ color: "#cdd5cf" }}>{createdToken.subLabel}</b>{" "}
                  and have them send it as the header{" "}
                  <code>X-Sub-Affiliate-Id</code> on every request.
                </p>
              </div>
            )}

            {showForm && (
              <Modal
                isOpen={showForm}
                onRequestClose={submitting ? undefined : closeForm}
                style={modalStyles}
                ariaHideApp={false}
                contentLabel="Sub-partner form"
              >
                <SubFeeForm
                  initial={editing}
                  onSubmit={handleSubmit}
                  onCancel={closeForm}
                  submitting={submitting}
                />
              </Modal>
            )}

            <div className="gtScroll">
              {subFees.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "16px 20px",
                    borderBottom: "1px solid rgba(255,255,255,.07)",
                  }}
                >
                  <span className="pTitle" style={{ fontSize: 15 }}>
                    Your sub-partners
                  </span>
                  <span style={{ fontSize: 12.5, color: "#7c847e" }}>
                    {stats.total} total · {stats.activeCount} active
                  </span>
                </div>
              )}
              {subFees.length === 0 ? (
                <div className="gtEmpty" style={{ padding: "60px 20px" }}>
                  <FontAwesomeIcon
                    icon={faUsersGear}
                    style={{ fontSize: 40, color: "#3d4641", marginBottom: 14, display: "block", marginLeft: "auto", marginRight: "auto" }}
                  />
                  <div style={{ color: "#a7b2ab", fontWeight: 600 }}>
                    No sub-partners yet.
                  </div>
                  <div style={{ fontSize: 13, marginTop: 6, maxWidth: 380, marginLeft: "auto", marginRight: "auto" }}>
                    Add your first sub-partner to issue a Sub-Affiliate-Id and
                    start tracking their swaps and fees.
                  </div>
                  <button
                    type="button"
                    className="btnPrimary sm"
                    style={{ marginTop: 18 }}
                    onClick={openAdd}
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add sub-partner
                  </button>
                </div>
              ) : (
                <div style={{ minWidth: 920 }}>
                  <div
                    className="gtHead"
                    style={{
                      gridTemplateColumns:
                        "minmax(150px,1.1fr) 130px 160px minmax(220px,1.3fr) 110px 96px",
                    }}
                  >
                    <span>Sub-partner</span>
                    <span>Your fee (bps)</span>
                    <span>Sub-partner fee (bps)</span>
                    <span>Sub-Affiliate-Id</span>
                    <span>Status</span>
                    <span className="r">Actions</span>
                  </div>
                  {subFees.map((sf) => {
                    const isActive = Number(sf.active) === 1;
                    return (
                      <div
                        key={sf.subAffiliateId}
                        className="gtRow"
                        style={{
                          gridTemplateColumns:
                            "minmax(150px,1.1fr) 130px 160px minmax(220px,1.3fr) 110px 96px",
                        }}
                      >
                        <div className="cellStrong" style={{ fontWeight: 700 }}>
                          {sf.subLabel}
                        </div>
                        <div className="cellStrong">
                          {sf.partnerFeeBps}
                          <small>{(sf.partnerFeeBps / 100).toFixed(2)}%</small>
                        </div>
                        <div className="cellStrong">
                          {Number(sf.subAffiliateFeeBps) || 0}
                          <small>
                            {((Number(sf.subAffiliateFeeBps) || 0) / 100).toFixed(2)}%
                          </small>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 9,
                            minWidth: 0,
                          }}
                        >
                          <span className="cellMono" title={sf.subAffiliateId}>
                            {sf.subAffiliateId}
                          </span>
                          <button
                            type="button"
                            className="iconBtn"
                            style={{ width: 28, height: 28 }}
                            onClick={() => copyToClipboard(sf.subAffiliateId)}
                            title="Copy Sub-Affiliate-Id"
                          >
                            <FontAwesomeIcon icon={faCopy} size="xs" />
                          </button>
                        </div>
                        <div>
                          <span className={`pill ${isActive ? "done" : "fail"}`}>
                            <span className="pd" />
                            {isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                        <div
                          className="r"
                          style={{ display: "flex", gap: 8, justifySelf: "end" }}
                        >
                          <button
                            type="button"
                            className="iconBtn edit"
                            style={{ width: 30, height: 30 }}
                            onClick={() => openEdit(sf)}
                            title="Edit"
                          >
                            <FontAwesomeIcon icon={faPenToSquare} size="xs" />
                          </button>
                          <button
                            type="button"
                            className="iconBtn danger"
                            style={{ width: 30, height: 30 }}
                            onClick={() => handleDelete(sf)}
                            title="Delete"
                          >
                            <FontAwesomeIcon icon={faTrashCan} size="xs" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubPartnerFees;
